import ParagraphLayoutBehavior from "./ParagraphLayoutBehavior";
import ParagraphBoundaryPolicy from "./ParagraphBoundaryPolicy";

const PARAGRAPH_KIND = ParagraphLayoutBehavior.KIND;
const PARAGRAPH_AFFINITY = ParagraphLayoutBehavior.AFFINITY;
const BREAK_STRENGTH = ParagraphBoundaryPolicy.BREAK_STRENGTH;

const NODE_TYPE = {
    WORD: "WordSequencerNode",
    PAUSE: "PauseSequencerNode",
    AUDIO_EVENT: "AudioEventSequencerNode",
};

const DEFAULT_MAX_SENTENCES_PER_PARAGRAPH = 4;
const DEFAULT_MAX_WORDS_PER_PARAGRAPH = 150;
const DEFAULT_MAX_NUMBER_OF_SEQUENCER_NODES_PER_PARAGRAPH = 200;
const DEFAULT_LONG_PAUSE_DURATION_IN_MILLISECONDS_TO_FORCE_BREAK = 0;
const SENTENCE_TERMINATOR_REGEX = /[.!?…]["')\]]*$/;

const ParagraphLayoutBuilder = {
    PARAGRAPH_KIND,
    PARAGRAPH_AFFINITY,
    DEFAULT_MAX_SENTENCES_PER_PARAGRAPH,
    DEFAULT_MAX_WORDS_PER_PARAGRAPH,
    DEFAULT_MAX_NUMBER_OF_SEQUENCER_NODES_PER_PARAGRAPH,
    DEFAULT_LONG_PAUSE_DURATION_IN_MILLISECONDS_TO_FORCE_BREAK,

    build : function(options){
        return this.analyze(options).paragraphLayout;
    },

    analyze : function(options){
        const resolvedOptions = this._getResolvedBuildOptions(options);
        const sequencerNodes = resolvedOptions.sequencerNodes;
        const paragraphBoundaryPolicy = resolvedOptions.paragraphBoundaryPolicy;
        const unknownSpeakerInfo = resolvedOptions.unknownSpeakerInfo;
        const defaultParagraphKind = resolvedOptions.defaultParagraphKind;
        const paragraphLayout = [];
        const pendingNodesToAssociateWithNextParagraph = [];
        const boundaryBeforeNodeCidMap = {};
        const paragraphIndexByNodeCidMap = {};
        let currentParagraph = null;
        let pendingBreakDecision = null;

        for(let i = 0; i < sequencerNodes.length; i++){
            const sequencerNode = sequencerNodes[i];
            const paragraphBehavior = this._getParagraphBehavior(
                sequencerNode,
                resolvedOptions.longPauseDurationInMillisecondsToForceBreak,
                paragraphBoundaryPolicy,
            );

            if(
                this._canNodeBeDeferredToFollowingParagraph(
                    sequencerNode,
                    paragraphBehavior,
                    paragraphBoundaryPolicy,
                )
            ){
                if(paragraphBehavior.breakBefore){
                    pendingBreakDecision = this._getBreakDecisionFromParagraphBehavior(paragraphBehavior, {
                        source: "before",
                        reasonIfMissing: "break-before-node",
                        useBreakAfterReasonAsFallback: true,
                    });
                }
                pendingNodesToAssociateWithNextParagraph.push(sequencerNode);
                continue;
            }

            const nextParagraphNodes = pendingNodesToAssociateWithNextParagraph.concat(sequencerNode);
            const paragraphStartNode = this._getParagraphBoundaryDecisionAnchorNode(nextParagraphNodes);
            const paragraphStartDecision = this._getParagraphStartDecision(
                paragraphBoundaryPolicy,
                {
                    sequencerNode: paragraphStartNode,
                    currentParagraph: currentParagraph,
                    nextParagraphNodes: nextParagraphNodes,
                    paragraphBehavior: paragraphBehavior,
                    pendingBreakDecision: pendingBreakDecision,
                    shouldBreakBeforeNextNode: pendingBreakDecision && pendingBreakDecision.shouldBreak,
                    unknownSpeakerInfo: resolvedOptions.unknownSpeakerInfo,
                    maxSentencesPerParagraph: resolvedOptions.maxSentencesPerParagraph,
                    maxWordsPerParagraph: resolvedOptions.maxWordsPerParagraph,
                    maxNumberOfSequencerNodesPerParagraph: resolvedOptions.maxNumberOfSequencerNodesPerParagraph,
                },
            );
            this._recordBoundaryDecisionForBatch(
                boundaryBeforeNodeCidMap,
                nextParagraphNodes,
                paragraphStartDecision,
                paragraphStartNode,
            );

            if(!currentParagraph || paragraphStartDecision.shouldBreak){
                currentParagraph = this._createParagraphDescriptorForNode(
                    sequencerNode,
                    defaultParagraphKind,
                    unknownSpeakerInfo,
                    sequencerNodes,
                    i,
                    paragraphBehavior,
                );
                paragraphLayout.push(currentParagraph);
            }

            if(pendingNodesToAssociateWithNextParagraph.length > 0){
                this._addNodesToParagraph(
                    currentParagraph,
                    pendingNodesToAssociateWithNextParagraph,
                    paragraphLayout.length - 1,
                    paragraphIndexByNodeCidMap,
                );
                pendingNodesToAssociateWithNextParagraph.length = 0;
            }

            this._addNodeToParagraph(currentParagraph, sequencerNode, paragraphLayout.length - 1, paragraphIndexByNodeCidMap);
            pendingBreakDecision = this._getBreakDecisionFromParagraphBehavior(paragraphBehavior, {
                source: "after",
                reasonIfMissing: paragraphBehavior && paragraphBehavior.isolateInOwnParagraph ? "isolated-node" : "break-after-node",
                useBreakBeforeReasonAsFallback: true,
            });

            if(paragraphBehavior.isolateInOwnParagraph){
                currentParagraph = null;
            }
        }

        if(pendingNodesToAssociateWithNextParagraph.length > 0){
            const fallbackNode = pendingNodesToAssociateWithNextParagraph[0];
            const trailingParagraphStartNode = this._getParagraphBoundaryDecisionAnchorNode(pendingNodesToAssociateWithNextParagraph);
            const trailingParagraphStartDecision = this._getParagraphStartDecision(
                paragraphBoundaryPolicy,
                {
                    sequencerNode: trailingParagraphStartNode,
                    currentParagraph: currentParagraph,
                    nextParagraphNodes: pendingNodesToAssociateWithNextParagraph,
                    paragraphBehavior: this._getParagraphBehavior(
                        trailingParagraphStartNode,
                        resolvedOptions.longPauseDurationInMillisecondsToForceBreak,
                        paragraphBoundaryPolicy,
                    ),
                    pendingBreakDecision: pendingBreakDecision,
                    shouldBreakBeforeNextNode: pendingBreakDecision && pendingBreakDecision.shouldBreak,
                    unknownSpeakerInfo: resolvedOptions.unknownSpeakerInfo,
                    maxSentencesPerParagraph: resolvedOptions.maxSentencesPerParagraph,
                    maxWordsPerParagraph: resolvedOptions.maxWordsPerParagraph,
                    maxNumberOfSequencerNodesPerParagraph: resolvedOptions.maxNumberOfSequencerNodesPerParagraph,
                },
            );
            this._recordBoundaryDecisionForBatch(
                boundaryBeforeNodeCidMap,
                pendingNodesToAssociateWithNextParagraph,
                trailingParagraphStartDecision,
                trailingParagraphStartNode,
            );

            if(!currentParagraph || trailingParagraphStartDecision.shouldBreak){
                // Use the boundary-decision anchor (the node that actually owns
                // any user paragraph-break preference in this trailing batch)
                // as the paragraph descriptor source, so speaker-heading lookup
                // lands on the meaningful node rather than the first deferred
                // one (which may be a deleted placeholder or punctuation).
                const descriptorNode = trailingParagraphStartNode || fallbackNode;
                currentParagraph = this._createParagraphDescriptorForNode(
                    descriptorNode,
                    defaultParagraphKind,
                    unknownSpeakerInfo,
                    sequencerNodes,
                    this._getTrailingDescriptorNodeIndex(
                        sequencerNodes,
                        pendingNodesToAssociateWithNextParagraph,
                        descriptorNode,
                    ),
                    this._getParagraphBehavior(
                        descriptorNode,
                        resolvedOptions.longPauseDurationInMillisecondsToForceBreak,
                        paragraphBoundaryPolicy,
                    ),
                );
                paragraphLayout.push(currentParagraph);
            }
            this._addNodesToParagraph(
                currentParagraph,
                pendingNodesToAssociateWithNextParagraph,
                paragraphLayout.length - 1,
                paragraphIndexByNodeCidMap,
            );
        }

        return {
            paragraphLayout: paragraphLayout.map((paragraphDescriptor)=>{
                return {
                    kind: paragraphDescriptor.kind,
                    speakerInfo: paragraphDescriptor.speakerInfo,
                    sequencerNodes: paragraphDescriptor.sequencerNodes.slice(),
                };
            }),
            boundaryBeforeNodeCidMap: boundaryBeforeNodeCidMap,
            paragraphIndexByNodeCidMap: paragraphIndexByNodeCidMap,
        };
    },

    _getNumericOption : function(options, key, defaultValue){
        const value = options ? options[key] : undefined;
        return Number.isFinite(value) ? value : defaultValue;
    },

    _getResolvedBuildOptions : function(options){
        const totalNumberOfSpeakerInfo = this._getNumericOption(options, "totalNumberOfSpeakerInfo", 0);
        return {
            sequencerNodes: options && Array.isArray(options.sequencerNodes) ? options.sequencerNodes : [],
            paragraphBoundaryPolicy: options ? options.paragraphBoundaryPolicy : null,
            totalNumberOfSpeakerInfo: totalNumberOfSpeakerInfo,
            unknownSpeakerInfo: options ? options.unknownSpeakerInfo : null,
            maxSentencesPerParagraph: this._getNumericOption(options, "maxSentencesPerParagraph", DEFAULT_MAX_SENTENCES_PER_PARAGRAPH),
            maxWordsPerParagraph: this._getNumericOption(
                options,
                "maxWordsPerParagraph",
                this._getNumericOption(options, "maxNumberOfWordsPerParagraph", DEFAULT_MAX_WORDS_PER_PARAGRAPH),
            ),
            maxNumberOfSequencerNodesPerParagraph: this._getNumericOption(
                options,
                "maxNumberOfSequencerNodesPerParagraph",
                DEFAULT_MAX_NUMBER_OF_SEQUENCER_NODES_PER_PARAGRAPH,
            ),
            longPauseDurationInMillisecondsToForceBreak: this._getNumericOption(
                options,
                "longPauseDurationInMillisecondsToForceBreak",
                DEFAULT_LONG_PAUSE_DURATION_IN_MILLISECONDS_TO_FORCE_BREAK,
            ),
            defaultParagraphKind: totalNumberOfSpeakerInfo > 0 ? PARAGRAPH_KIND.SPEAKER : PARAGRAPH_KIND.UNLABELED,
        };
    },

    _getParagraphBehavior : function(sequencerNode, longPauseDurationInMillisecondsToForceBreak, paragraphBoundaryPolicy){
        if(paragraphBoundaryPolicy && paragraphBoundaryPolicy.resolveNodeBehavior){
            return paragraphBoundaryPolicy.resolveNodeBehavior({
                sequencerNode: sequencerNode,
                longPauseDurationInMillisecondsToForceBreak: longPauseDurationInMillisecondsToForceBreak,
                getNodeType: this._getNodeType.bind(this),
                isPauseSequencerNode: this._isPauseSequencerNode.bind(this),
                getNodeDurationInMilliseconds: this._getNodeDurationInMilliseconds.bind(this),
            });
        }
        const paragraphBehavior = ParagraphLayoutBehavior.normalize(
            sequencerNode && sequencerNode.getParagraphLayoutBehavior ? sequencerNode.getParagraphLayoutBehavior() : null,
        );

        if(
            !paragraphBehavior.breakAfter
            && longPauseDurationInMillisecondsToForceBreak > 0
            && this._isPauseSequencerNode(sequencerNode)
            && this._getNodeDurationInMilliseconds(sequencerNode) >= longPauseDurationInMillisecondsToForceBreak
        ){
            paragraphBehavior.breakAfter = true;
        }

        return paragraphBehavior;
    },

    _createParagraphDescriptorForNode : function(sequencerNode, defaultParagraphKind, unknownSpeakerInfo, sequencerNodes, nodeIndex, paragraphBehavior){
        const paragraphKind = paragraphBehavior && paragraphBehavior.paragraphKind ? paragraphBehavior.paragraphKind : defaultParagraphKind;
        if(paragraphKind === PARAGRAPH_KIND.SPEAKER){
            return {
                kind: PARAGRAPH_KIND.SPEAKER,
                speakerInfo: paragraphBehavior && paragraphBehavior.speakerInfoOverride
                    ? paragraphBehavior.speakerInfoOverride
                    : this._resolveSpeakerInfoForSpeakerParagraph(sequencerNode, unknownSpeakerInfo, sequencerNodes, nodeIndex),
                sequencerNodes: [],
                _sentencesCount: 0,
                _wordCount: 0,
            };
        }
        return {
            kind: PARAGRAPH_KIND.UNLABELED,
            speakerInfo: null,
            sequencerNodes: [],
            _sentencesCount: 0,
            _wordCount: 0,
        };
    },

    _resolveSpeakerInfoForSpeakerParagraph : function(sequencerNode, unknownSpeakerInfo, sequencerNodes, nodeIndex){
        if(sequencerNode && sequencerNode.getSpeakerInfo && sequencerNode.getSpeakerInfo()){
            return sequencerNode.getSpeakerInfo();
        }
        if(sequencerNode && sequencerNode.canBeAssociactedWithAnySpeaker && sequencerNode.canBeAssociactedWithAnySpeaker()){
            const nextSpeakerInfo = this._findNextSpeakerInfo(sequencerNodes, nodeIndex + 1);
            if(nextSpeakerInfo){
                return nextSpeakerInfo;
            }
        }
        return unknownSpeakerInfo;
    },

    _findNextSpeakerInfo : function(sequencerNodes, startIndex){
        for(let i = startIndex; i < sequencerNodes.length; i++){
            const speakerInfo = sequencerNodes[i] && sequencerNodes[i].getSpeakerInfo ? sequencerNodes[i].getSpeakerInfo() : null;
            if(speakerInfo){
                return speakerInfo;
            }
        }
        return null;
    },

    _getTrailingDescriptorNodeIndex : function(sequencerNodes, trailingSequencerNodes, descriptorNode){
        const trailingBatchStartIndex = Math.max(sequencerNodes.length - trailingSequencerNodes.length, 0);
        const descriptorNodeIndexInTrailingBatch = trailingSequencerNodes.indexOf(descriptorNode);
        if(typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production"){
            console.assert(
                descriptorNodeIndexInTrailingBatch !== -1,
                "[ParagraphLayoutBuilder] trailing descriptor node must come from the pending trailing batch",
            );
        }
        return descriptorNodeIndexInTrailingBatch === -1
            ? trailingBatchStartIndex
            : trailingBatchStartIndex + descriptorNodeIndexInTrailingBatch;
    },

    _canNodeBeDeferredToFollowingParagraph : function(sequencerNode, paragraphBehavior, paragraphBoundaryPolicy){
        if(paragraphBoundaryPolicy && paragraphBoundaryPolicy.canDeferNodeToFollowingParagraph){
            return !!paragraphBoundaryPolicy.canDeferNodeToFollowingParagraph({
                sequencerNode: sequencerNode,
                paragraphBehavior: paragraphBehavior,
                getExplicitSpeakerInfo: this._getExplicitSpeakerInfo.bind(this),
            });
        }
        if(this._getExplicitSpeakerInfo(sequencerNode)){
            return false;
        }
        if(paragraphBehavior && paragraphBehavior.speakerInfoOverride){
            return false;
        }
        if(paragraphBehavior && paragraphBehavior.paragraphKind){
            return false;
        }
        return true;
    },

    _getParagraphStartDecision : function(paragraphBoundaryPolicy, context){
        const policyContext = Object.assign({}, context, {
            canNodesBeAddedToParagraph: (sequencerNodes, paragraphDescriptor)=>{
                return this._canNodesBeAddedToParagraph(
                    sequencerNodes,
                    paragraphDescriptor,
                    context.unknownSpeakerInfo,
                    context.maxSentencesPerParagraph,
                    context.maxWordsPerParagraph,
                    context.maxNumberOfSequencerNodesPerParagraph,
                );
            },
            getParagraphCompatibilityDecision: (sequencerNodes, paragraphDescriptor)=>{
                return this._getParagraphCompatibilityDecision(
                    sequencerNodes,
                    paragraphDescriptor,
                    context.unknownSpeakerInfo,
                    context.maxSentencesPerParagraph,
                    context.maxWordsPerParagraph,
                    context.maxNumberOfSequencerNodesPerParagraph,
                );
            },
        });
        if(paragraphBoundaryPolicy && typeof paragraphBoundaryPolicy.getParagraphBreakDecision === "function"){
            return this._normalizeBreakDecision(paragraphBoundaryPolicy.getParagraphBreakDecision(policyContext));
        }
        if(paragraphBoundaryPolicy && typeof paragraphBoundaryPolicy.shouldStartNewParagraph === "function"){
            return this._createBreakDecision(
                paragraphBoundaryPolicy.shouldStartNewParagraph(policyContext),
                null,
                null,
            );
        }
        const compatibilityDecision = this._getParagraphCompatibilityDecision(
            context.nextParagraphNodes,
            context.currentParagraph,
            context.unknownSpeakerInfo,
            context.maxSentencesPerParagraph,
            context.maxWordsPerParagraph,
            context.maxNumberOfSequencerNodesPerParagraph,
        );
        return this._createBreakDecision(
            !context.currentParagraph
                || !!(context.pendingBreakDecision && context.pendingBreakDecision.shouldBreak)
                || context.paragraphBehavior.breakBefore
                || context.paragraphBehavior.isolateInOwnParagraph
                || compatibilityDecision.canAdd === false,
            compatibilityDecision.canAdd === false ? compatibilityDecision.reason : null,
            compatibilityDecision.canAdd === false ? compatibilityDecision.strength : null,
        );
    },

    _getExplicitSpeakerInfo : function(sequencerNode){
        return sequencerNode && sequencerNode.getSpeakerInfo ? sequencerNode.getSpeakerInfo() : null;
    },

    _getParagraphBoundaryPreferenceForNode : function(sequencerNode){
        if(!sequencerNode){
            return null;
        }
        if(typeof sequencerNode.getParagraphBoundaryPreference === "function"){
            return sequencerNode.getParagraphBoundaryPreference();
        }
        return sequencerNode.get ? sequencerNode.get("paragraphBoundaryPreference") : null;
    },

    _getParagraphBoundaryDecisionAnchorNode : function(sequencerNodes){
        if(!Array.isArray(sequencerNodes) || sequencerNodes.length === 0){
            return null;
        }
        for(let i = 0; i < sequencerNodes.length; i++){
            if(this._getParagraphBoundaryPreferenceForNode(sequencerNodes[i])){
                return sequencerNodes[i];
            }
        }
        return sequencerNodes[sequencerNodes.length - 1];
    },

    _canNodeBeAddedToParagraph : function(sequencerNode, paragraphDescriptor, unknownSpeakerInfo, maxSentencesPerParagraph, maxWordsPerParagraph, maxNumberOfSequencerNodesPerParagraph){
        return this._getNodeParagraphCompatibilityDecision(
            sequencerNode,
            paragraphDescriptor,
            unknownSpeakerInfo,
            maxSentencesPerParagraph,
            maxWordsPerParagraph,
            maxNumberOfSequencerNodesPerParagraph,
        ).canAdd;
    },

    _canNodesBeAddedToParagraph : function(sequencerNodes, paragraphDescriptor, unknownSpeakerInfo, maxSentencesPerParagraph, maxWordsPerParagraph, maxNumberOfSequencerNodesPerParagraph){
        return this._getParagraphCompatibilityDecision(
            sequencerNodes,
            paragraphDescriptor,
            unknownSpeakerInfo,
            maxSentencesPerParagraph,
            maxWordsPerParagraph,
            maxNumberOfSequencerNodesPerParagraph,
        ).canAdd;
    },

    _getParagraphCompatibilityDecision : function(sequencerNodes, paragraphDescriptor, unknownSpeakerInfo, maxSentencesPerParagraph, maxWordsPerParagraph, maxNumberOfSequencerNodesPerParagraph){
        if(!paragraphDescriptor){
            return {
                canAdd: false,
                reason: "missing-current-paragraph",
                strength: BREAK_STRENGTH.HARD,
            };
        }
        const simulatedParagraphDescriptor = this._cloneParagraphDescriptor(paragraphDescriptor);
        for(let i = 0; i < sequencerNodes.length; i++){
            const compatibilityDecision = this._getNodeParagraphCompatibilityDecision(
                sequencerNodes[i],
                simulatedParagraphDescriptor,
                unknownSpeakerInfo,
                maxSentencesPerParagraph,
                maxWordsPerParagraph,
                maxNumberOfSequencerNodesPerParagraph,
            );
            if(!compatibilityDecision.canAdd){
                return compatibilityDecision;
            }
            this._addNodeToParagraph(simulatedParagraphDescriptor, sequencerNodes[i]);
        }
        return {
            canAdd: true,
            reason: null,
            strength: null,
        };
    },

    _getNodeParagraphCompatibilityDecision : function(sequencerNode, paragraphDescriptor, unknownSpeakerInfo, maxSentencesPerParagraph, maxWordsPerParagraph, maxNumberOfSequencerNodesPerParagraph){
        if(!paragraphDescriptor){
            return {
                canAdd: false,
                reason: "missing-current-paragraph",
                strength: BREAK_STRENGTH.HARD,
            };
        }
        const structuralCompatibilityDecision = paragraphDescriptor.kind === PARAGRAPH_KIND.SPEAKER
            ? this._getSpeakerParagraphCompatibilityDecision(sequencerNode, paragraphDescriptor, unknownSpeakerInfo)
            : this._getUnlabeledParagraphCompatibilityDecision(sequencerNode);
        if(!structuralCompatibilityDecision.canAdd){
            return structuralCompatibilityDecision;
        }
        if(maxNumberOfSequencerNodesPerParagraph > 0 && paragraphDescriptor.sequencerNodes.length >= maxNumberOfSequencerNodesPerParagraph){
            return {
                canAdd: false,
                reason: "max-nodes-per-paragraph",
                strength: BREAK_STRENGTH.SOFT,
            };
        }
        if(this._isWordSequencerNode(sequencerNode) && maxWordsPerParagraph > 0 && paragraphDescriptor._wordCount >= maxWordsPerParagraph){
            return {
                canAdd: false,
                reason: "max-words-per-paragraph",
                strength: BREAK_STRENGTH.SOFT,
            };
        }
        if(maxSentencesPerParagraph > 0 && this._isFlowNodeForParagraph(sequencerNode) && paragraphDescriptor._sentencesCount >= maxSentencesPerParagraph){
            return {
                canAdd: false,
                reason: "max-sentences-per-paragraph",
                strength: BREAK_STRENGTH.SOFT,
            };
        }
        return structuralCompatibilityDecision;
    },

    _getSpeakerParagraphCompatibilityDecision : function(sequencerNode, paragraphDescriptor, unknownSpeakerInfo){
        const speakerInfo = sequencerNode && sequencerNode.getSpeakerInfo ? sequencerNode.getSpeakerInfo() : null;
        const paragraphSpeakerInfo = paragraphDescriptor ? paragraphDescriptor.speakerInfo : null;
        if(
            speakerInfo
            && paragraphSpeakerInfo
            && typeof speakerInfo.getSpeakerId === "function"
            && typeof paragraphSpeakerInfo.getSpeakerId === "function"
        ){
            return {
                canAdd: speakerInfo.getSpeakerId() === paragraphSpeakerInfo.getSpeakerId(),
                reason: speakerInfo.getSpeakerId() === paragraphSpeakerInfo.getSpeakerId() ? null : "speaker-mismatch",
                strength: speakerInfo.getSpeakerId() === paragraphSpeakerInfo.getSpeakerId() ? null : BREAK_STRENGTH.HARD,
            };
        }
        if(speakerInfo){
            return {
                canAdd: false,
                reason: "speaker-mismatch",
                strength: BREAK_STRENGTH.HARD,
            };
        }
        if(
            paragraphSpeakerInfo
            && unknownSpeakerInfo
            && typeof paragraphSpeakerInfo.getSpeakerId === "function"
            && typeof unknownSpeakerInfo.getSpeakerId === "function"
            && paragraphSpeakerInfo.getSpeakerId() === unknownSpeakerInfo.getSpeakerId()
        ){
            return {
                canAdd: true,
                reason: null,
                strength: null,
            };
        }
        return {
            canAdd: !!(
                sequencerNode
                && typeof sequencerNode.canBeAssociactedWithAnySpeaker === "function"
                && sequencerNode.canBeAssociactedWithAnySpeaker()
            ),
            reason: (
                sequencerNode
                && typeof sequencerNode.canBeAssociactedWithAnySpeaker === "function"
                && sequencerNode.canBeAssociactedWithAnySpeaker()
            )
                ? null
                : "speaker-required",
            strength: (
                sequencerNode
                && typeof sequencerNode.canBeAssociactedWithAnySpeaker === "function"
                && sequencerNode.canBeAssociactedWithAnySpeaker()
            )
                ? null
                : BREAK_STRENGTH.HARD,
        };
    },

    _getUnlabeledParagraphCompatibilityDecision : function(sequencerNode){
        const canAdd = !(
            sequencerNode
            && sequencerNode.needToBeAssociactedWithASpeaker
            && sequencerNode.needToBeAssociactedWithASpeaker()
            && sequencerNode.getSpeakerInfo
            && sequencerNode.getSpeakerInfo()
        );
        return {
            canAdd: canAdd,
            reason: canAdd ? null : "speaker-required",
            strength: canAdd ? null : BREAK_STRENGTH.HARD,
        };
    },

    _isFlowNodeForParagraph : function(sequencerNode){
        return !!sequencerNode && (
            this._isWordSequencerNode(sequencerNode)
            || this._isPauseSequencerNode(sequencerNode)
            || this._isAudioEventSequencerNode(sequencerNode)
        );
    },

    _getNodeType : function(sequencerNode){
        if(!sequencerNode){
            return null;
        }
        const constructor = sequencerNode.getConstructor ? sequencerNode.getConstructor() : sequencerNode.constructor;
        if(constructor && constructor.CLASSNAME){
            return constructor.CLASSNAME;
        }
        if(sequencerNode.CLASSNAME){
            return sequencerNode.CLASSNAME;
        }
        if(sequencerNode.type){
            return sequencerNode.type;
        }
        if(sequencerNode.getType){
            return sequencerNode.getType();
        }
        return sequencerNode.get && sequencerNode.get("type") ? sequencerNode.get("type") : null;
    },

    _isWordSequencerNode : function(sequencerNode){
        return this._getNodeType(sequencerNode) === NODE_TYPE.WORD;
    },

    _isPauseSequencerNode : function(sequencerNode){
        return this._getNodeType(sequencerNode) === NODE_TYPE.PAUSE;
    },

    _isAudioEventSequencerNode : function(sequencerNode){
        return this._getNodeType(sequencerNode) === NODE_TYPE.AUDIO_EVENT;
    },

    _getNodeDurationInMilliseconds : function(sequencerNode){
        if(!sequencerNode){
            return 0;
        }
        const duration = sequencerNode.getDuration ? sequencerNode.getDuration() : undefined;
        if(Number.isFinite(duration)){
            return duration;
        }
        const audioSegment = sequencerNode.getAudioSegment ? sequencerNode.getAudioSegment() : null;
        const audioSegmentDuration = audioSegment && audioSegment.getDuration ? audioSegment.getDuration() : 0;
        return Number.isFinite(audioSegmentDuration) ? audioSegmentDuration : 0;
    },

    _isNodeEndingSentence : function(sequencerNode){
        const content = sequencerNode && sequencerNode.getContent ? sequencerNode.getContent() : null;
        return !!(this._isWordSequencerNode(sequencerNode) && content && SENTENCE_TERMINATOR_REGEX.test(content.trim()));
    },

    _addNodesToParagraph : function(paragraphDescriptor, sequencerNodes, paragraphIndex, paragraphIndexByNodeCidMap){
        for(let i = 0; i < sequencerNodes.length; i++){
            this._addNodeToParagraph(paragraphDescriptor, sequencerNodes[i], paragraphIndex, paragraphIndexByNodeCidMap);
        }
    },

    _cloneParagraphDescriptor : function(paragraphDescriptor){
        return {
            kind: paragraphDescriptor.kind,
            speakerInfo: paragraphDescriptor.speakerInfo,
            sequencerNodes: paragraphDescriptor.sequencerNodes.slice(),
            _sentencesCount: paragraphDescriptor._sentencesCount,
            _wordCount: paragraphDescriptor._wordCount,
        };
    },

    _addNodeToParagraph : function(paragraphDescriptor, sequencerNode, paragraphIndex, paragraphIndexByNodeCidMap){
        paragraphDescriptor.sequencerNodes.push(sequencerNode);
        if(
            Number.isFinite(paragraphIndex)
            && paragraphIndexByNodeCidMap
            && sequencerNode
            && sequencerNode.cid
        ){
            paragraphIndexByNodeCidMap[sequencerNode.cid] = paragraphIndex;
        }
        if(this._isWordSequencerNode(sequencerNode)){
            paragraphDescriptor._wordCount = paragraphDescriptor._wordCount + 1;
        }
        if(this._isNodeEndingSentence(sequencerNode)){
            paragraphDescriptor._sentencesCount = paragraphDescriptor._sentencesCount + 1;
        }
    },

    _normalizeBreakStrength : function(strength){
        if(strength === BREAK_STRENGTH.HARD || strength === BREAK_STRENGTH.SOFT){
            return strength;
        }
        return null;
    },

    _createBreakDecision : function(shouldBreak, reason, strength){
        return {
            shouldBreak: !!shouldBreak,
            reason: reason || null,
            strength: shouldBreak ? (this._normalizeBreakStrength(strength) || BREAK_STRENGTH.HARD) : null,
        };
    },

    _normalizeBreakDecision : function(decision){
        if(!decision){
            return this._createBreakDecision(false, null, null);
        }
        return this._createBreakDecision(decision.shouldBreak, decision.reason, decision.strength);
    },

    _getBreakDecisionFromParagraphBehavior : function(paragraphBehavior, options){
        const normalizedOptions = options || {};
        const source = normalizedOptions.source === "before" ? "before" : "after";
        const shouldUseBreakBefore = source === "before";
        const shouldBreak = paragraphBehavior && (
            paragraphBehavior.isolateInOwnParagraph
            || (shouldUseBreakBefore ? paragraphBehavior.breakBefore : paragraphBehavior.breakAfter)
        );
        if(!paragraphBehavior || !shouldBreak){
            return this._createBreakDecision(false, null, null);
        }
        const reason = shouldUseBreakBefore
            ? paragraphBehavior.breakBeforeReason
            : paragraphBehavior.breakAfterReason;
        const fallbackReason = normalizedOptions.reasonIfMissing || "paragraph-boundary";
        const strength = shouldUseBreakBefore
            ? paragraphBehavior.breakBeforeStrength
            : paragraphBehavior.breakAfterStrength;
        const fallbackStrength = paragraphBehavior.isolateInOwnParagraph
            ? paragraphBehavior.breakAfterStrength || paragraphBehavior.breakBeforeStrength || BREAK_STRENGTH.HARD
            : BREAK_STRENGTH.HARD;
        return this._createBreakDecision(
            true,
            reason
                || (normalizedOptions.useBreakBeforeReasonAsFallback ? paragraphBehavior.breakBeforeReason : null)
                || (normalizedOptions.useBreakAfterReasonAsFallback ? paragraphBehavior.breakAfterReason : null)
                || fallbackReason,
            strength
                || (normalizedOptions.useBreakBeforeStrengthAsFallback ? paragraphBehavior.breakBeforeStrength : null)
                || (normalizedOptions.useBreakAfterStrengthAsFallback ? paragraphBehavior.breakAfterStrength : null)
                || fallbackStrength,
        );
    },

    _recordBoundaryDecisionForBatch : function(boundaryBeforeNodeCidMap, sequencerNodes, paragraphStartDecision, anchorSequencerNode){
        if(!Array.isArray(sequencerNodes) || sequencerNodes.length === 0){
            return;
        }
        const anchorNode = anchorSequencerNode || sequencerNodes[0];
        this._setBoundaryDecisionForNode(boundaryBeforeNodeCidMap, sequencerNodes[0], paragraphStartDecision);
        if(anchorNode !== sequencerNodes[0]){
            this._setBoundaryDecisionForNode(boundaryBeforeNodeCidMap, anchorNode, paragraphStartDecision);
        }
        for(let i = 1; i < sequencerNodes.length; i++){
            if(sequencerNodes[i] === anchorNode){
                continue;
            }
            this._setBoundaryDecisionForNode(
                boundaryBeforeNodeCidMap,
                sequencerNodes[i],
                this._createBreakDecision(false, null, null),
            );
        }
    },

    _setBoundaryDecisionForNode : function(boundaryBeforeNodeCidMap, sequencerNode, decision){
        if(!boundaryBeforeNodeCidMap || !sequencerNode || !sequencerNode.cid){
            return;
        }
        boundaryBeforeNodeCidMap[sequencerNode.cid] = this._normalizeBreakDecision(decision);
    },
};

export default ParagraphLayoutBuilder;
